import axios from '../utils/axios';

/**
 * Profile signature API response
 */
export interface ProfileSignatureResponse {
  status: number;
  signatureBase64?: string; // "data:image/png;base64,..."
  message?: string;
}

/**
 * Upload researcher's signature image
 * 
 * @param file - Image file (PNG, JPG, JPEG)
 * @returns Promise with upload response
 * 
 * @example
 * ```typescript
 * const file = fileInput.files[0];
 * const response = await uploadSignature(file);
 * console.log(response.message); // "Signature uploaded successfully"
 * ```
 */
export const uploadSignature = async (file: File): Promise<ProfileSignatureResponse> => {
  try {
    const formData = new FormData();
    formData.append('signature', file);

    const response = await axios.post(
      `${import.meta.env.VITE_APP_API_URL}/researcher/profile/signature`,
      formData,
      {
        headers: {
          'Authorization': localStorage.getItem('jwt'),
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return {
      status: response.status,
      message: response.data.message || 'Signature uploaded successfully',
    };
  } catch (error: any) {
    // Handle specific error codes
    if (error.response) {
      const status = error.response.status;
      const errorCode = error.response.data?.code;
      
      if (status === 400) {
        throw new Error('Invalid file type or size. Please upload a PNG or JPG image under 10MB.');
      } else if (status === 404) {
        throw new Error('Researcher profile not found. Please try logging in again.');
      } else if (status === 413) {
        throw new Error('File is too large. Please upload an image under 10MB.');
      } else {
        throw new Error(error.response.data?.message || 'Failed to upload signature. Please try again.');
      }
    }
    
    // Network or other errors
    throw new Error('Network error. Please check your connection and try again.');
  }
};

/**
 * Get researcher's current signature (if exists)
 * 
 * @returns Promise with signature data (base64 encoded image)
 * 
 * @example
 * ```typescript
 * try {
 *   const response = await getOwnSignature();
 *   if (response.signatureBase64) {
 *     imgElement.src = response.signatureBase64;
 *   }
 * } catch (error) {
 *   console.log('No signature uploaded yet');
 * }
 * ```
 */
export const getOwnSignature = async (): Promise<ProfileSignatureResponse> => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_APP_API_URL}/researcher/profile/signature`,
      {
        headers: {
          'Authorization': localStorage.getItem('jwt'),
        },
      }
    );

    return {
      status: response.status,
      signatureBase64: response.data.signatureBase64,
    };
  } catch (error: any) {
    // Handle specific error codes
    if (error.response) {
      const status = error.response.status;
      
      if (status === 404) {
        // No signature uploaded yet - this is not an error, just return null
        return {
          status: 404,
          signatureBase64: undefined,
        };
      } else if (status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else {
        throw new Error(error.response.data?.message || 'Failed to fetch signature.');
      }
    }
    
    // Network or other errors
    throw new Error('Network error. Please check your connection and try again.');
  }
};

/**
 * Remove researcher's signature
 * 
 * @returns Promise with deletion response
 */
export const removeSignature = async (): Promise<ProfileSignatureResponse> => {
  try {
    const response = await axios.delete(
      `${import.meta.env.VITE_APP_API_URL}/researcher/profile/signature`,
      {
        headers: {
          'Authorization': localStorage.getItem('jwt'),
        },
      }
    );

    return {
      status: response.status,
      message: response.data.message || 'Signature removed successfully',
    };
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data?.message || 'Failed to remove signature.');
    }
    throw new Error('Network error. Please check your connection and try again.');
  }
};

